import { type ComboboxItem, Select, Table } from '@mantine/core';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { isEmpty, times } from 'remeda';

import type { CsvData, MatchedColumns } from '../types.ts';

type ColumnMatchingProps = {
  isInvoiceMode: boolean;
  csvData: CsvData;
  matchedColumns: MatchedColumns;
  mandatoryFields: Array<ComboboxItem>;
  unmatchedMandatoryFields: Array<ComboboxItem>;
  optionalFields: Array<ComboboxItem>;
  unmatchedOptionalFields: Array<ComboboxItem>;
  handleMatchColumn: (matchedField: MatchedColumns[number], colIndex: number) => void;
};

export const ColumnMatchingRow = ({
  isInvoiceMode,
  csvData,
  matchedColumns,
  mandatoryFields,
  unmatchedMandatoryFields,
  optionalFields,
  unmatchedOptionalFields,
  handleMatchColumn,
}: ColumnMatchingProps) => {
  const { t } = useTranslation();
  const { custom_variables: _customVariables } = useCompanyConfiguration();

  const customVariables = _customVariables
    .filter(variable => variable.model_type === (isInvoiceMode ? 'invoice' : 'debtor'))
    .map(({ name, column_name }) => ({ value: column_name, label: name }));

  return (
    <Table.Tr style={theme => ({ backgroundColor: theme.colors.purple[2] })}>
      <Table.Th />
      {times(csvData[0].length, index => (
        <Table.Th pos="sticky" key={index}>
          <Select
            clearable
            searchable
            w={150}
            comboboxProps={{
              width: 250,
              shadow: 'lg',
            }}
            placeholder={t(i18nKeys.SETTINGS.INVOICING.FOOTER.SELECT_FIELD)}
            data={[
              {
                group: t(i18nKeys.MODULES.IMPORT.REQUIRED_FIELDS),
                items: [
                  ...mandatoryFields.filter(({ value }) => value === matchedColumns[index]),
                  ...unmatchedMandatoryFields,
                ],
              },
              {
                group: t(i18nKeys.MODULES.IMPORT.AVAILABLE_FIELDS),
                items: [
                  ...optionalFields.filter(({ value }) => value === matchedColumns[index]),
                  ...unmatchedOptionalFields,
                ],
              },
              ...(!isEmpty(customVariables)
                ? [
                    {
                      group: t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE),
                      items: customVariables,
                    },
                  ]
                : []),
            ]}
            value={matchedColumns[index]}
            onChange={value => handleMatchColumn(value, index)}
          />
        </Table.Th>
      ))}
    </Table.Tr>
  );
};
